import {useEffect, useRef, useState} from 'react'
import {Link as RouterLink, useLocation} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {trackEvent} from '../../common/analytics/trackEvent'
import {TOOL_LINKS} from '../../common/constants/toolLinks'
import './NavToolsMenu.css'

/** "Tools" nav dropdown consolidating the free utility pages — replaces flat
 * per-tool nav links so the header doesn't keep growing as tools are added.
 * Reuses NavAvatar.tsx's interaction pattern (click-outside + Escape close). */
const NavToolsMenu = () => {
  const {t} = useTranslation()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const handleToggle = () => {
    const next = !open
    setOpen(next)
    if (next) trackEvent('tools_menu_opened')
  }

  const isToolActive = TOOL_LINKS.some((link) => location.pathname === link.to)

  return (
    <div className="nav-tools-wrap" ref={wrapRef}>
      <button
        type="button"
        className={isToolActive ? 'nav-tools-trigger is-active' : 'nav-tools-trigger'}
        onClick={handleToggle}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {t('landing.nav.tools')}
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={open ? 'nav-tools-chevron is-open' : 'nav-tools-chevron'}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="nav-tools-menu" role="menu">
          {TOOL_LINKS.map((link) => (
            <RouterLink
              key={link.to}
              to={link.to}
              className={location.pathname === link.to ? 'nav-tools-item is-active' : 'nav-tools-item'}
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              {t(link.labelKey)}
            </RouterLink>
          ))}
          <RouterLink to="/tools" className="nav-tools-view-all" role="menuitem" onClick={() => setOpen(false)}>
            {t('landing.nav.viewAllTools')}
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M5 12h14" />
              <path d="M13 5l7 7-7 7" />
            </svg>
          </RouterLink>
        </div>
      )}
    </div>
  )
}

export default NavToolsMenu
